"use client";

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import { EDGE_LEFT, EDGE_TAIL_INSET } from "./pageWidth";
import {
  thumbSrc as ikThumbSrc,
  thumbSrcSet as ikThumbSrcSet,
} from "./thumbnails";
import useRevealOnScroll from "./useRevealOnScroll";

type Episode = {
  id: string;
  episode: string;
  name: string;
  designation: string;
  company: string;
  /** Bare ImageKit filename — the transform is appended at render, see thumbSrc. */
  thumbnail: string;
  videoUrl: string;
};

const IK =
  "https://ik.imagekit.io/mileseducation/miles_website/home_2026/partnerships";

const thumbSrc = (path: string, width: number) => ikThumbSrc(IK, path, width);
const thumbSrcSet = (path: string) => ikThumbSrcSet(IK, path);

/* Two screens' worth up front; the rest arrive as the track is scrolled.
   See ./useRevealOnScroll. */
const INITIAL_VISIBLE = 6;
const REVEAL_BATCH = 4;

/**
 * Sizing for these lives in ./thumbnails — one ladder shared by all four
 * carousels, so the widths and quality can be tuned in a single place.
 */
const episodes: Episode[] = [
  {
    id: "ep-5",
    episode: "EP 05",
    name: "CA Abhinav Vijayaraghavan",
    designation: "Managing Director",
    company: "Withum",
    thumbnail: "ep_5.webp",
    videoUrl: "https://youtu.be/UCAE_WxnAH4",
  },
  {
    id: "ep-13",
    episode: "EP 13",
    name: "Karthick Venkatakrishnan",
    designation: "Managing Director",
    company: "EisnerAmper India",
    thumbnail: "Karthick%20Venkatakrishnan.png",
    videoUrl: "https://youtu.be/Biu1FtPuqYw",
  },
  {
    id: "ep-1",
    episode: "EP 01",
    name: "Shrenik Shah",
    designation: "Managing Director",
    company: "Armanino",
    thumbnail: "ep_1.webp",
    videoUrl: "https://youtu.be/tWG5Jq2fYT8",
  },
  {
    id: "ep-11",
    episode: "EP 11",
    name: "Dr. Charles J A",
    designation: "Director - LIAC",
    company: "Loyola College",
    thumbnail: "Dr.%20Charles%20J%20A.png",
    videoUrl: "https://youtu.be/BchHmm_rv3o",
  },
  {
    id: "ep-4",
    episode: "EP 04",
    name: "Chung Tham",
    designation: "GDS Assurance - Financial Services Audit Leader",
    company: "EY",
    thumbnail: "ep_4.jpg",
    videoUrl: "https://youtu.be/Cq3PEw9ryqw",
  },
  {
    id: "ep-14",
    episode: "EP 14",
    name: "Shrenik Shah",
    designation: "Managing Director",
    company: "Armanino India",
    thumbnail: "Shrenik%20Shah.png",
    videoUrl: "https://youtu.be/M1l1vWXov7M",
  },
  {
    id: "ep-6",
    episode: "EP 06",
    name: "Rajesh Asopa",
    designation: "VP",
    company: "MNC Bank",
    thumbnail: "Rajesh%20Asopa%20(2).png",
    videoUrl: "https://youtu.be/jezxQBu6fSU",
  },
  {
    id: "ep-8",
    episode: "EP 08",
    name: "Mugdha Paranjape",
    designation: "Deputy Director",
    company: "Alliance University",
    thumbnail: "Mugdha%20Paranjape.png",
    videoUrl: "https://youtu.be/BawZJyuOSu8",
  },
  {
    id: "ep-3",
    episode: "EP 03",
    name: "Vishal Agarwal",
    designation: "Executive managing Director",
    company: "Citrin Cooperman India LLP",
    thumbnail: "ep_3.webp",
    videoUrl: "https://youtu.be/0A297kqov8c",
  },
  {
    id: "ep-9",
    episode: "EP 09",
    name: "Rakesh Jain",
    designation: "Talent Acquisition Leader",
    company: "Grant Thornton INDUS",
    thumbnail: "Rakesh%20Jain.png",
    videoUrl: "https://youtu.be/dNhEHHQ4Tk8",
  },
  {
    id: "ep-2",
    episode: "EP 02",
    name: "Karthick Venkatakrishnan",
    designation: "Managing Director",
    company: "EisnerAmper India",
    thumbnail: "ep_2.webp",
    videoUrl: "https://youtu.be/2O_yc14-c04",
  },
  {
    id: "ep-10",
    episode: "EP 10",
    name: "Nigel John",
    designation: "Talent Acquisition Head",
    company: "Withum",
    thumbnail: "Nigel%20John.png",
    videoUrl: "https://youtu.be/cifFsr_5UXc",
  },
  {
    id: "ep-7",
    episode: "EP 07",
    name: "Suman Dutta",
    designation: "VP ERP Implementation",
    company: "Diageo",
    thumbnail: "Suman%20Dutta.png",
    videoUrl: "https://youtu.be/YPO4iB_E8ak",
  },
  {
    id: "ep-12",
    episode: "EP 12",
    name: "Vishnu Patwari",
    designation: "Founding Executive Director",
    company: "BDO Rise",
    thumbnail: "Vishnu%20Patwari.png",
    videoUrl: "https://youtu.be/IiFce1pHcK0",
  },
];

/** Accepts youtu.be/<id>, /watch?v=<id> or /embed/<id> — share params are ignored. */
const getYouTubeId = (url: string): string => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|v\/))([\w-]{11})/,
  );
  return match ? match[1] : "";
};

const PlayGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="ml-1 h-5 w-5">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const CloseGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className="h-4 w-4"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/** Mounted only once a card is clicked, so no YouTube JS or iframe on load. */
const VideoModal = ({
  episode,
  onClose,
}: {
  episode: Episode;
  onClose: () => void;
}) => {
  const [mounted, setMounted] = useState(false);

  useModalScrollLock(true, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!mounted) return null;

  // Portalled to <body> so the overlay can't be trapped by an ancestor
  // stacking context.
  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${episode.episode} video`}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative w-full max-w-[900px]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-10 right-0 flex h-8 w-8 items-center justify-center rounded-full border border-white/60 text-white hover:border-white"
        >
          <CloseGlyph />
        </button>
        <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${getYouTubeId(
              episode.videoUrl,
            )}?autoplay=1&rel=0`}
            title={`${episode.episode} — ${episode.name}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="h-full w-full border-0"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

const EpisodeCard = ({
  episode,
  onPlay,
}: {
  episode: Episode;
  onPlay: (_episode: Episode) => void;
}) => (
  <div className="h-full overflow-hidden rounded-2xl bg-[#0F0F0F]">
    <button
      type="button"
      onClick={() => onPlay(episode)}
      aria-label={`Play ${episode.episode} with ${episode.name}`}
      className="group relative block aspect-video w-full overflow-hidden border-b border-[#2A85FF]"
    >
      {/* The masters are all exactly 16:9, so 1280x720 as the intrinsic ratio
          matches the aspect-video box and nothing shifts as they decode. */}
      <img
        src={thumbSrc(episode.thumbnail, 640)}
        srcSet={thumbSrcSet(episode.thumbnail)}
        sizes="(max-width: 768px) 76vw, 29vw"
        alt={`${episode.episode} — ${episode.name}, ${episode.company}`}
        width={1280}
        height={720}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />

      <span className="absolute left-3 top-3 rounded-full bg-[#2A85FF] px-3 py-1 text-[11px] font-semibold text-white">
        {episode.episode}
      </span>

      {/* Play affordance — revealed on hover, and while focused via keyboard
          since there's no hover on touch devices. */}
      <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-[#0F0F0F]">
          <PlayGlyph />
        </span>
      </span>
    </button>

    <div className="px-4 py-3">
      <p className="text-[15px] font-bold text-[#FFBA08]">{episode.name}</p>
      <p className="mt-1 text-sm font-bold text-white">{episode.designation}</p>
      <p className="mt-1 text-sm text-[#CACACA]">{episode.company}</p>
    </div>
  </div>
);

const PartnershipMncs: React.FC = () => {
  const [activeEpisode, setActiveEpisode] = useState<Episode | null>(null);

  const { trackRef, visibleCount } = useRevealOnScroll(episodes.length, {
    initial: INITIAL_VISIBLE,
    batch: REVEAL_BATCH,
  });

  const closeModal = useCallback(() => setActiveEpisode(null), []);

  return (
    <section className="w-full bg-black pt-[50px] pb-6 md:py-[50px]">
      <div className="px-4 text-center md:px-8">
        <h2 className="text-[18px] font-bold leading-snug text-white md:text-[24px]">
          Big4, 220+ MNCs & CPA Firms in the U.S.
          <br className="md:hidden block" /> trust Miles
        </h2>
      </div>

      {/*
        A plain overflow-x scroller: horizontal trackpad swipes, touch swipes,
        drag-scroll and arrow keys are all handled by the browser, and a
        vertical gesture is left alone so the page scrolls as normal.
        overscroll-x-contain stops the gesture chaining into back-navigation.
        Mobile snaps card to card; desktop is free so the glide runs smoothly.
        Mobile keeps bleeding off the right edge; on desktop EDGE_TAIL_INSET
        closes the track with the same gutter EDGE_LEFT opens it with.
      */}
      <div
        ref={trackRef}
        className={`scrollbar-hide mt-6 flex snap-x snap-mandatory items-stretch overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-pl-4 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-[#2A85FF] sm:scroll-pl-6 md:mt-8 md:snap-none ${EDGE_LEFT} ${EDGE_TAIL_INSET}`}
        // A scrollable region has to be reachable by keyboard for the arrow
        // keys to have anywhere to land — the rule doesn't model that case.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="region"
        aria-label="Podcast episodes — scroll sideways to browse"
      >
        {episodes.slice(0, visibleCount).map((episode) => (
          <div
            key={episode.id}
            /* pr-3.5 rather than px-2: the left padding was stacking on top of
               the track's own inset, so the first card sat further in than the
               heading. The gap between cards now comes entirely from the right
               side, matching OperatingModel, and because the padding stays
               inside the border box the 28.571% still gives 3.5 cards. */
            className="w-[76%] shrink-0 snap-start pr-3.5 md:w-[28.571%]"
          >
            <EpisodeCard episode={episode} onPlay={setActiveEpisode} />
          </div>
        ))}
      </div>

      {activeEpisode && (
        <VideoModal episode={activeEpisode} onClose={closeModal} />
      )}
    </section>
  );
};

export default PartnershipMncs;
